import "./styles.css";

import {
  AnimatedSprite,
  Application,
  Assets,
  Container,
  Graphics,
  Sprite,
  Text,
  Texture,
} from "pixi.js";

const VIEW_WIDTH = 1280;
const VIEW_HEIGHT = 720;
const LEVEL_WIDTH = 4300;
const FLOOR_Y = 632;

const GRAVITY = 2250;
const RUN_SPEED = 360;
const JUMP_SPEED = 820;
const SPRING_SPEED = 1050;
const MAX_FALL_SPEED = 1350;

const ASSETS = {
  backdrop: new URL("../assets/generated/moonhop-backdrop.png", import.meta.url).href,
  maxRun: [
    new URL("../assets/generated/max-run/run-1.png", import.meta.url).href,
    new URL("../assets/generated/max-run/run-2.png", import.meta.url).href,
    new URL("../assets/generated/max-run/run-3.png", import.meta.url).href,
    new URL("../assets/generated/max-run/run-4.png", import.meta.url).href,
  ],
  collectible: new URL("../assets/generated/props/prop-1.png", import.meta.url).href,
  hazard: new URL("../assets/generated/props/prop-2.png", import.meta.url).href,
  gate: new URL("../assets/generated/props/prop-3.png", import.meta.url).href,
  spring: new URL("../assets/generated/props/prop-4.png", import.meta.url).href,
};

type Rect = {
  x: number;
  y: number;
  w: number;
  h: number;
};

type Collectible = {
  x: number;
  y: number;
  collected: boolean;
  sprite: Sprite;
  phase: number;
};

type Hazard = {
  x: number;
  y: number;
  rect: Rect;
  sprite: Sprite;
};

type SpringPad = {
  x: number;
  y: number;
  rect: Rect;
  sprite: Sprite;
  cooldown: number;
};

type Particle = {
  node: Graphics;
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  radius: number;
  color: number;
};

type TextureSet = {
  runFrames: Texture[];
  collectible: Texture;
  hazard: Texture;
  gate: Texture;
  spring: Texture;
  backdrop: Texture;
};

type DebugState = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  cameraX: number;
  collected: number;
  totalCollectibles: number;
  energy: number;
  win: boolean;
  status: string;
};

declare global {
  interface Window {
    __MAX_MOONHOP_DEBUG__?: {
      state: () => DebugState;
      setPlayer: (x: number, y: number) => void;
      collectAll: () => void;
      reset: () => void;
    };
  }
}

const platforms: Rect[] = [
  { x: -120, y: FLOOR_Y, w: 900, h: 120 },
  { x: 870, y: 572, w: 260, h: 44 },
  { x: 1220, y: 512, w: 360, h: 44 },
  { x: 1700, y: FLOOR_Y, w: 520, h: 120 },
  { x: 2290, y: 546, w: 260, h: 44 },
  { x: 2655, y: 488, w: 310, h: 44 },
  { x: 3070, y: 578, w: 250, h: 44 },
  { x: 3420, y: FLOOR_Y, w: 1020, h: 120 },
];

const collectibleLayout = [
  { x: 360, y: 494 },
  { x: 760, y: 472 },
  { x: 1010, y: 444 },
  { x: 1370, y: 384 },
  { x: 1850, y: 502 },
  { x: 2155, y: 498 },
  { x: 2415, y: 414 },
  { x: 2820, y: 356 },
  { x: 3160, y: 446 },
  { x: 3720, y: 496 },
  { x: 3950, y: 496 },
];

const hazardLayout = [
  { x: 780, y: FLOOR_Y },
  { x: 1768, y: FLOOR_Y },
  { x: 2040, y: FLOOR_Y },
  { x: 3120, y: 578 },
  { x: 3580, y: FLOOR_Y },
];

const springLayout = [
  { x: 1188, y: FLOOR_Y },
  { x: 2240, y: FLOOR_Y },
  { x: 3000, y: FLOOR_Y },
];

const clamp = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(max, value));

const intersects = (a: Rect, b: Rect) =>
  a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;

class MaxMoonhop {
  private readonly app: Application;
  private readonly textures: TextureSet;
  private readonly root = new Container({ label: "scaled-root" });
  private readonly backgroundLayer = new Container({ label: "background-layer" });
  private readonly worldLayer = new Container({ label: "world-layer", sortableChildren: true });
  private readonly fxLayer = new Container({ label: "fx-layer" });
  private readonly uiLayer = new Container({ label: "ui-layer" });

  private readonly backdrop: Sprite;
  private readonly playerSprite: AnimatedSprite;
  private readonly player = {
    x: 96,
    y: FLOOR_Y - 126,
    w: 62,
    h: 126,
    vx: 0,
    vy: 0,
    onGround: false,
    facing: 1,
    invulnerable: 0,
  };

  private readonly keys = new Set<string>();
  private readonly jumpLatch = { pressed: false };
  private readonly collectibles: Collectible[] = [];
  private readonly hazards: Hazard[] = [];
  private readonly springs: SpringPad[] = [];
  private readonly particles: Particle[] = [];

  private readonly titleText = new Text({
    text: "MAX MOONHOP",
    style: {
      fontFamily: "Arial, sans-serif",
      fontSize: 28,
      fontWeight: "900",
      fill: 0xf8fbff,
      stroke: { color: 0x102135, width: 5 },
      dropShadow: { color: 0x000000, blur: 4, distance: 2, alpha: 0.4 },
    },
  });

  private readonly carrotsText = new Text({
    text: "",
    style: {
      fontFamily: "Arial, sans-serif",
      fontSize: 22,
      fontWeight: "800",
      fill: 0xffde6b,
      stroke: { color: 0x2b210c, width: 4 },
    },
  });

  private readonly energyText = new Text({
    text: "",
    style: {
      fontFamily: "Arial, sans-serif",
      fontSize: 22,
      fontWeight: "800",
      fill: 0x9be7ff,
      stroke: { color: 0x0b2033, width: 4 },
    },
  });

  private readonly statusText = new Text({
    text: "READY",
    style: {
      fontFamily: "Arial, sans-serif",
      fontSize: 34,
      fontWeight: "900",
      fill: 0xffffff,
      stroke: { color: 0x102135, width: 6 },
      dropShadow: { color: 0x000000, blur: 5, distance: 3, alpha: 0.35 },
    },
  });

  private readonly fadeOverlay = new Graphics();

  private cameraX = 0;
  private scale = 1;
  private collectedCount = 0;
  private energy = 3;
  private elapsed = 0;
  private shakeTime = 0;
  private shakeStrength = 0;
  private statusTimer = 2.0;
  private win = false;
  private recovering = 0;

  public constructor(app: Application, textures: TextureSet) {
    this.app = app;
    this.textures = textures;

    this.backdrop = new Sprite({ texture: this.textures.backdrop });
    this.playerSprite = new AnimatedSprite({
      textures: this.textures.runFrames,
      animationSpeed: 0.18,
      autoPlay: true,
      loop: true,
      anchor: { x: 0.5, y: 1 },
    });
  }

  public start(): void {
    this.app.stage.addChild(this.root);
    this.root.addChild(this.backgroundLayer, this.worldLayer, this.fxLayer, this.uiLayer);

    this.buildBackground();
    this.buildLevel();
    this.buildPlayer();
    this.buildUi();
    this.bindInput();
    this.resetRun();
    this.resize();

    window.addEventListener("resize", () => this.resize());
    this.app.ticker.add((ticker) => this.update(ticker.deltaMS / 1000));
  }

  public getDebugState(): DebugState {
    return {
      x: this.player.x,
      y: this.player.y,
      vx: this.player.vx,
      vy: this.player.vy,
      cameraX: this.cameraX,
      collected: this.collectedCount,
      totalCollectibles: this.collectibles.length,
      energy: this.energy,
      win: this.win,
      status: this.statusText.text,
    };
  }

  public debugSetPlayer(x: number, y: number): void {
    this.player.x = clamp(x, 0, LEVEL_WIDTH - this.player.w);
    this.player.y = y;
    this.player.vx = 0;
    this.player.vy = 0;
    this.player.onGround = false;
    this.player.invulnerable = 0;
    this.recovering = 0;
    this.win = false;
  }

  public debugCollectAll(): void {
    for (const collectible of this.collectibles) {
      collectible.collected = true;
      collectible.sprite.visible = false;
    }
    this.collectedCount = this.collectibles.length;
    this.updateHud();
  }

  private buildBackground(): void {
    const bgScale = VIEW_HEIGHT / this.backdrop.texture.height;
    this.backdrop.scale.set(bgScale);
    this.backdrop.y = 0;
    this.backgroundLayer.addChild(this.backdrop);

    const glow = new Graphics()
      .circle(990, 110, 42)
      .fill({ color: 0xfff0a5, alpha: 0.17 })
      .circle(990, 110, 18)
      .fill({ color: 0xffffff, alpha: 0.18 });
    glow.zIndex = 3;
    this.backgroundLayer.addChild(glow);
  }

  private buildLevel(): void {
    for (const platform of platforms) {
      this.worldLayer.addChild(this.platformGraphic(platform));
    }

    this.addDecorativeGrass();

    for (const item of collectibleLayout) {
      const sprite = new Sprite({
        texture: this.textures.collectible,
        anchor: 0.5,
        scale: 0.23,
      });
      sprite.position.set(item.x, item.y);
      sprite.zIndex = 20;
      this.worldLayer.addChild(sprite);
      this.collectibles.push({
        x: item.x,
        y: item.y,
        collected: false,
        sprite,
        phase: Math.random() * Math.PI * 2,
      });
    }

    for (const hazard of hazardLayout) {
      const sprite = new Sprite({
        texture: this.textures.hazard,
        anchor: { x: 0.5, y: 1 },
        scale: 0.31,
      });
      sprite.position.set(hazard.x, hazard.y + 8);
      sprite.zIndex = 16;
      this.worldLayer.addChild(sprite);
      this.hazards.push({
        x: hazard.x,
        y: hazard.y,
        sprite,
        rect: { x: hazard.x - 48, y: hazard.y - 54, w: 96, h: 48 },
      });
    }

    for (const spring of springLayout) {
      const sprite = new Sprite({
        texture: this.textures.spring,
        anchor: { x: 0.5, y: 1 },
        scale: 0.27,
      });
      sprite.position.set(spring.x, spring.y + 4);
      sprite.zIndex = 17;
      this.worldLayer.addChild(sprite);
      this.springs.push({
        x: spring.x,
        y: spring.y,
        sprite,
        cooldown: 0,
        rect: { x: spring.x - 45, y: spring.y - 38, w: 90, h: 28 },
      });
    }

    const gate = new Sprite({
      texture: this.textures.gate,
      anchor: { x: 0.5, y: 1 },
      scale: 0.52,
    });
    gate.position.set(4120, FLOOR_Y + 8);
    gate.zIndex = 18;
    gate.label = "finish-gate";
    this.worldLayer.addChild(gate);
  }

  private platformGraphic(platform: Rect): Graphics {
    const g = new Graphics({ label: "platform" });
    const top = 0x79c86f;
    const lip = 0x486a54;
    const stone = 0x334d64;
    const shadow = 0x1b2e42;

    g.roundRect(platform.x, platform.y, platform.w, platform.h, 14)
      .fill(stone)
      .stroke({ width: 4, color: shadow, alpha: 0.9 });
    g.roundRect(platform.x, platform.y - 9, platform.w, 24, 12).fill(top);
    g.rect(platform.x, platform.y + 12, platform.w, 12).fill({ color: lip, alpha: 0.6 });

    const chunks = Math.max(3, Math.floor(platform.w / 120));
    for (let index = 0; index < chunks; index += 1) {
      const cx = platform.x + 34 + index * (platform.w / chunks);
      const cy = platform.y + 38 + ((index * 17) % 34);
      g.roundRect(cx, cy, 58, 13, 6).fill({ color: 0x49677d, alpha: 0.55 });
    }

    return g;
  }

  private addDecorativeGrass(): void {
    const tufts = [
      [210, FLOOR_Y],
      [520, FLOOR_Y],
      [970, 572],
      [1410, 512],
      [1885, FLOOR_Y],
      [2375, 546],
      [2790, 488],
      [3190, 578],
      [3650, FLOOR_Y],
      [4050, FLOOR_Y],
    ];

    for (const [x, y] of tufts) {
      const tuft = new Graphics({ label: "grass-tuft" });
      tuft.moveTo(x - 22, y + 1)
        .lineTo(x - 7, y - 25)
        .lineTo(x, y + 1)
        .lineTo(x + 12, y - 30)
        .lineTo(x + 23, y + 1)
        .fill({ color: 0x91d86d, alpha: 0.9 });
      tuft.zIndex = 13;
      this.worldLayer.addChild(tuft);
    }
  }

  private buildPlayer(): void {
    this.playerSprite.scale.set(0.57);
    this.playerSprite.zIndex = 50;
    this.playerSprite.play();
    this.worldLayer.addChild(this.playerSprite);
  }

  private buildUi(): void {
    this.titleText.position.set(28, 24);
    this.carrotsText.position.set(28, 62);
    this.energyText.position.set(1090, 26);
    this.statusText.anchor.set(0.5);
    this.statusText.position.set(VIEW_WIDTH / 2, 84);

    this.fadeOverlay.rect(0, 0, VIEW_WIDTH, VIEW_HEIGHT).fill({
      color: 0xffffff,
      alpha: 0,
    });
    this.fadeOverlay.zIndex = 200;

    this.uiLayer.addChild(
      this.titleText,
      this.carrotsText,
      this.energyText,
      this.statusText,
      this.fadeOverlay,
    );
  }

  private bindInput(): void {
    window.addEventListener("keydown", (event) => {
      if (["ArrowLeft", "ArrowRight", "ArrowUp", "Space"].includes(event.code)) {
        event.preventDefault();
      }
      this.keys.add(event.code);
      if (["ArrowUp", "KeyW", "Space"].includes(event.code)) {
        this.jumpLatch.pressed = true;
      }
      if (event.code === "KeyR") {
        this.resetRun();
      }
    });

    window.addEventListener("keyup", (event) => {
      this.keys.delete(event.code);
    });
  }

  public resetRun(): void {
    this.player.x = 96;
    this.player.y = FLOOR_Y - this.player.h;
    this.player.vx = 0;
    this.player.vy = 0;
    this.player.onGround = false;
    this.player.facing = 1;
    this.player.invulnerable = 0;
    this.energy = 3;
    this.cameraX = 0;
    this.collectedCount = 0;
    this.win = false;
    this.recovering = 0;
    this.shakeTime = 0;
    this.shakeStrength = 0;
    this.statusText.text = "READY";
    this.statusTimer = 1.5;
    for (const collectible of this.collectibles) {
      collectible.collected = false;
      collectible.sprite.visible = true;
      collectible.sprite.alpha = 1;
      collectible.sprite.scale.set(0.23);
    }
    for (const spring of this.springs) {
      spring.cooldown = 0;
      spring.sprite.scale.set(0.27);
    }
    this.clearParticles();
    this.updateHud();
  }

  private update(dtRaw: number): void {
    const dt = Math.min(dtRaw, 0.18);
    this.elapsed += dt;

    if (this.recovering > 0) {
      this.recovering -= dt;
      if (this.recovering <= 0) {
        this.resetRun();
      }
    } else if (!this.win) {
      this.updatePlayer(dt);
      this.updateInteractions(dt);
    }

    this.updateCamera();
    this.updateVisuals(dt);
    this.updateHud();
  }

  private updatePlayer(dt: number): void {
    const left = this.keys.has("ArrowLeft") || this.keys.has("KeyA");
    const right = this.keys.has("ArrowRight") || this.keys.has("KeyD");
    const move = Number(right) - Number(left);

    this.player.vx = move * RUN_SPEED;
    if (move !== 0) {
      this.player.facing = move;
    }

    if (this.jumpLatch.pressed && this.player.onGround) {
      this.player.vy = -JUMP_SPEED;
      this.player.onGround = false;
      this.burst(this.player.x + this.player.w / 2, this.player.y + this.player.h, 0xb8f7ff, 9);
    }
    this.jumpLatch.pressed = false;

    this.player.vy = clamp(this.player.vy + GRAVITY * dt, -SPRING_SPEED, MAX_FALL_SPEED);
    this.moveAndCollide(this.player.vx * dt, 0);
    this.player.onGround = false;
    this.moveAndCollide(0, this.player.vy * dt);

    if (this.player.y > VIEW_HEIGHT + 160) {
      this.hurtPlayer(0, true);
    }

    this.player.x = clamp(this.player.x, 0, LEVEL_WIDTH - this.player.w);
  }

  private moveAndCollide(dx: number, dy: number): void {
    this.player.x += dx;
    this.player.y += dy;

    const rect = this.playerRect();
    for (const platform of platforms) {
      if (!intersects(rect, platform)) {
        continue;
      }

      if (dx > 0) {
        this.player.x = platform.x - this.player.w;
      } else if (dx < 0) {
        this.player.x = platform.x + platform.w;
      }

      if (dy > 0) {
        this.player.y = platform.y - this.player.h;
        this.player.vy = 0;
        this.player.onGround = true;
      } else if (dy < 0) {
        this.player.y = platform.y + platform.h;
        this.player.vy = 0;
      }

      rect.x = this.player.x;
      rect.y = this.player.y;
    }
  }

  private updateInteractions(dt: number): void {
    const playerRect = this.playerRect();

    for (const collectible of this.collectibles) {
      if (collectible.collected) {
        continue;
      }

      const rect = { x: collectible.x - 28, y: collectible.y - 34, w: 56, h: 68 };
      if (intersects(playerRect, rect)) {
        collectible.collected = true;
        collectible.sprite.visible = false;
        this.collectedCount += 1;
        this.statusText.text = "BRIGHT!";
        this.statusTimer = 0.85;
        this.burst(collectible.x, collectible.y, 0xffdc5e, 15);
        this.shake(0.08, 2.5);
      }
    }

    for (const spring of this.springs) {
      spring.cooldown = Math.max(0, spring.cooldown - dt);
      if (spring.cooldown <= 0 && this.player.vy > 0 && intersects(playerRect, spring.rect)) {
        spring.cooldown = 0.45;
        this.player.vy = -SPRING_SPEED;
        this.player.onGround = false;
        spring.sprite.scale.set(0.34, 0.2);
        this.statusText.text = "BOOST!";
        this.statusTimer = 0.8;
        this.burst(spring.x, spring.y - 26, 0x7ef6ff, 18);
        this.shake(0.12, 4);
      }
    }

    if (this.player.invulnerable > 0) {
      this.player.invulnerable -= dt;
    } else {
      for (const hazard of this.hazards) {
        if (intersects(playerRect, hazard.rect)) {
          this.hurtPlayer(this.player.x < hazard.x ? -1 : 1, false);
          break;
        }
      }
    }

    const gateRect = { x: 4050, y: FLOOR_Y - 198, w: 150, h: 190 };
    if (intersects(playerRect, gateRect)) {
      if (this.collectedCount >= this.collectibles.length) {
        this.win = true;
        this.player.vx = 0;
        this.player.vy = 0;
        this.statusText.text = "MOON GATE OPEN";
        this.statusTimer = 1000;
        this.burst(4120, FLOOR_Y - 118, 0xfff0a5, 36);
        this.burst(this.player.x + 30, this.player.y + 50, 0x9be7ff, 24);
        this.shake(0.28, 6);
      } else {
        const left = this.collectibles.length - this.collectedCount;
        this.statusText.text = `${left} MORE`;
        this.statusTimer = 1.1;
      }
    }
  }

  private hurtPlayer(direction: number, fell: boolean): void {
    this.energy -= 1;
    this.player.invulnerable = 1.2;
    this.player.vx = direction * 250;
    this.player.vy = fell ? 0 : -560;
    this.statusText.text = fell ? "DRIFT!" : "OUCH!";
    this.statusTimer = 1.0;
    this.burst(this.player.x + this.player.w / 2, this.player.y + 62, 0xff5577, 22);
    this.shake(0.24, 9);

    if (this.energy <= 0 || fell) {
      this.recovering = 1.1;
      this.statusText.text = "RETRY";
      this.statusTimer = 1.1;
    }
  }

  private updateCamera(): void {
    const target = this.player.x + this.player.w / 2 - VIEW_WIDTH * 0.42;
    this.cameraX += (clamp(target, 0, LEVEL_WIDTH - VIEW_WIDTH) - this.cameraX) * 0.12;

    let shakeX = 0;
    let shakeY = 0;
    if (this.shakeTime > 0) {
      const strength = this.shakeStrength * (this.shakeTime / 0.3);
      shakeX = (Math.random() - 0.5) * strength;
      shakeY = (Math.random() - 0.5) * strength;
    }

    this.worldLayer.position.set(-Math.round(this.cameraX) + shakeX, shakeY);
    this.fxLayer.position.set(-Math.round(this.cameraX) + shakeX, shakeY);

    const bgSlack = Math.max(0, this.backdrop.width - VIEW_WIDTH);
    const progress = this.cameraX / (LEVEL_WIDTH - VIEW_WIDTH);
    this.backdrop.x = -bgSlack * progress;
  }

  private updateVisuals(dt: number): void {
    this.playerSprite.position.set(
      this.player.x + this.player.w / 2,
      this.player.y + this.player.h + 8,
    );
    this.playerSprite.scale.x = Math.abs(this.playerSprite.scale.x) * this.player.facing;

    const moving = Math.abs(this.player.vx) > 2 && this.player.onGround && !this.win;
    if (moving) {
      if (!this.playerSprite.playing) {
        this.playerSprite.play();
      }
      this.playerSprite.animationSpeed = 0.17;
    } else {
      this.playerSprite.gotoAndStop(this.player.onGround ? 3 : 1);
    }

    if (this.player.invulnerable > 0) {
      this.playerSprite.alpha = Math.sin(this.elapsed * 34) > 0 ? 0.62 : 1;
      this.playerSprite.tint = 0xffd4df;
    } else {
      this.playerSprite.alpha = 1;
      this.playerSprite.tint = 0xffffff;
    }

    for (const collectible of this.collectibles) {
      if (collectible.collected) {
        continue;
      }
      const bob = Math.sin(this.elapsed * 3.4 + collectible.phase) * 9;
      collectible.sprite.y = collectible.y + bob;
      collectible.sprite.rotation = Math.sin(this.elapsed * 2.1 + collectible.phase) * 0.05;
    }

    for (const hazard of this.hazards) {
      hazard.sprite.rotation = Math.sin(this.elapsed * 2.6 + hazard.x) * 0.025;
      hazard.sprite.tint = Math.sin(this.elapsed * 6 + hazard.x) > 0.65 ? 0xffbfd0 : 0xffffff;
    }

    for (const spring of this.springs) {
      if (spring.cooldown <= 0) {
        spring.sprite.scale.x += (0.27 - spring.sprite.scale.x) * 0.18;
        spring.sprite.scale.y += (0.27 - spring.sprite.scale.y) * 0.18;
      } else {
        spring.sprite.scale.x += (0.34 - spring.sprite.scale.x) * 0.22;
        spring.sprite.scale.y += (0.2 - spring.sprite.scale.y) * 0.22;
      }
    }

    this.updateParticles(dt);

    if (this.shakeTime > 0) {
      this.shakeTime = Math.max(0, this.shakeTime - dt);
    }

    if (this.statusTimer > 0) {
      this.statusTimer -= dt;
      this.statusText.alpha = 1;
      this.statusText.scale.set(1 + Math.sin(this.elapsed * 8) * 0.015);
    } else {
      this.statusText.alpha += (0 - this.statusText.alpha) * 0.08;
    }
  }

  private updateParticles(dt: number): void {
    for (let index = this.particles.length - 1; index >= 0; index -= 1) {
      const p = this.particles[index];
      p.life -= dt;
      p.vy += 640 * dt;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      const alpha = clamp(p.life / p.maxLife, 0, 1);
      p.node.clear();
      p.node.circle(p.x, p.y, p.radius * (0.7 + alpha * 0.6)).fill({
        color: p.color,
        alpha,
      });
      if (p.life <= 0) {
        p.node.destroy();
        this.particles.splice(index, 1);
      }
    }
  }

  private burst(x: number, y: number, color: number, count: number): void {
    for (let i = 0; i < count; i += 1) {
      const angle = (Math.PI * 2 * i) / count + Math.random() * 0.3;
      const speed = 95 + Math.random() * 230;
      const node = new Graphics({ label: "particle" });
      node.zIndex = 100;
      this.fxLayer.addChild(node);
      this.particles.push({
        node,
        x,
        y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 90,
        life: 0.42 + Math.random() * 0.35,
        maxLife: 0.7,
        radius: 3 + Math.random() * 4,
        color,
      });
    }
  }

  private shake(time: number, strength: number): void {
    this.shakeTime = Math.max(this.shakeTime, time);
    this.shakeStrength = Math.max(this.shakeStrength, strength);
  }

  private clearParticles(): void {
    for (const particle of this.particles) {
      particle.node.destroy();
    }
    this.particles.length = 0;
  }

  private updateHud(): void {
    this.carrotsText.text = `STAR CARROTS ${this.collectedCount}/${this.collectibles.length}`;
    this.energyText.text = `ENERGY ${this.energy}`;
  }

  private playerRect(): Rect {
    return {
      x: this.player.x,
      y: this.player.y,
      w: this.player.w,
      h: this.player.h,
    };
  }

  private resize(): void {
    this.scale = Math.min(
      this.app.screen.width / VIEW_WIDTH,
      this.app.screen.height / VIEW_HEIGHT,
    );
    this.root.scale.set(this.scale);
    this.root.position.set(
      Math.round((this.app.screen.width - VIEW_WIDTH * this.scale) / 2),
      Math.round((this.app.screen.height - VIEW_HEIGHT * this.scale) / 2),
    );
  }
}

const loadTextures = async (): Promise<TextureSet> => {
  await Assets.load([
    { alias: "backdrop", src: ASSETS.backdrop },
    { alias: "max-run-1", src: ASSETS.maxRun[0] },
    { alias: "max-run-2", src: ASSETS.maxRun[1] },
    { alias: "max-run-3", src: ASSETS.maxRun[2] },
    { alias: "max-run-4", src: ASSETS.maxRun[3] },
    { alias: "collectible", src: ASSETS.collectible },
    { alias: "hazard", src: ASSETS.hazard },
    { alias: "gate", src: ASSETS.gate },
    { alias: "spring", src: ASSETS.spring },
  ]);

  return {
    backdrop: Assets.get("backdrop") as Texture,
    runFrames: [
      Assets.get("max-run-1") as Texture,
      Assets.get("max-run-2") as Texture,
      Assets.get("max-run-3") as Texture,
      Assets.get("max-run-4") as Texture,
    ],
    collectible: Assets.get("collectible") as Texture,
    hazard: Assets.get("hazard") as Texture,
    gate: Assets.get("gate") as Texture,
    spring: Assets.get("spring") as Texture,
  };
};

const bootstrap = async () => {
  const mount = document.querySelector<HTMLElement>("#app");
  if (!mount) {
    throw new Error("Missing #app mount");
  }

  const app = new Application();
  await app.init({
    resizeTo: mount,
    background: 0x07101f,
    antialias: true,
    autoDensity: true,
    resolution: Math.min(window.devicePixelRatio || 1, 2),
    preference: "webgl",
  });

  app.canvas.tabIndex = 0;
  app.canvas.setAttribute("aria-label", "Max Moonhop game canvas");
  mount.appendChild(app.canvas);
  app.canvas.focus();

  const textures = await loadTextures();
  const game = new MaxMoonhop(app, textures);
  game.start();

  if (new URLSearchParams(window.location.search).has("qa")) {
    window.__MAX_MOONHOP_DEBUG__ = {
      state: () => game.getDebugState(),
      setPlayer: (x: number, y: number) => game.debugSetPlayer(x, y),
      collectAll: () => game.debugCollectAll(),
      reset: () => game.resetRun(),
    };
  }
};

bootstrap().catch((error) => {
  const mount = document.querySelector<HTMLElement>("#app");
  if (mount) {
    mount.textContent = error instanceof Error ? error.message : String(error);
  }
  console.error(error);
});
